import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Brain, Loader, PlusCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '../store/sessionStore';
import { supabase } from '../lib/supabase';
import { requestAIAnalysis } from '../lib/api';
import { LetterGrid } from '../components/LetterGrid';
import { ParticipantList } from '../components/ParticipantList';
import { SessionTimer } from '../components/SessionTimer';
import { SessionStatus } from '../components/SessionStatus';
import { SummaryModal } from '../components/SummaryModal';
import { ScrollingAlphabet } from '../components/ScrollingAlphabet';
import { ExportButton } from '../components/ExportButton';
import { ShareButton } from '../components/ShareButton';
import { useLanguage } from '../lib/i18n/context';
import { isValidLanguage } from '../lib/i18n/config';
import type { Entry, Participant, Session, AISummary } from '../lib/types';

export function BrainstormingPage() {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { language } = useLanguage();
  const isViewOnly = searchParams.get('view') === 'true';
  const isJoining = searchParams.get('join') === 'true';
  const paymentStatus = searchParams.get('payment');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AISummary | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [nickname, setNickname] = useState('');
  const [isJoiningSession, setIsJoiningSession] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  
  const session = useSessionStore((state) => state.session);
  const participant = useSessionStore((state) => state.participant);
  const entries = useSessionStore((state) => state.entries);
  const participants = useSessionStore((state) => state.participants);
  const setSession = useSessionStore((state) => state.setSession);
  const setParticipant = useSessionStore((state) => state.setParticipant);
  const setEntries = useSessionStore((state) => state.setEntries);
  const setParticipants = useSessionStore((state) => state.setParticipants);
  const addEntry = useSessionStore((state) => state.addEntry);
  const addParticipant = useSessionStore((state) => state.addParticipant);
  const updateParticipant = useSessionStore((state) => state.updateParticipant);
  const removeParticipant = useSessionStore((state) => state.removeParticipant);
  const reset = useSessionStore((state) => state.reset);

  // Check for payment success/cancelled
  useEffect(() => {
    if (paymentStatus) {
      // Remove payment status from URL to prevent confusion on refresh
      searchParams.delete('payment');
      setSearchParams(searchParams);
      
      // If payment was successful, check for the summary after a delay
      // to allow webhook processing
      if (paymentStatus === 'success' && sessionId) {
        setTimeout(async () => {
          try {
            const { data } = await supabase
              .from('ai_summaries')
              .select('*')
              .eq('session_id', sessionId)
              .eq('payment_status', 'completed')
              .maybeSingle();
              
            if (data) {
              setSummary(data);
              setShowSummaryModal(true);
            }
          } catch (err) {
            console.error('Error checking summary after payment:', err);
          }
        }, 2000);
      }
    }
  }, [paymentStatus, sessionId, searchParams, setSearchParams]);

  // Handle language redirection if needed
  useEffect(() => {
    // Only run this logic for direct session URLs
    const pathParts = location.pathname.split('/').filter(Boolean);
    
    // If this is a direct path like /session/xyz (no language prefix)
    if (pathParts[0] === 'session' && pathParts.length === 2) {
      // Direct session URL - handle without redirection
    }
  }, [location]);

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    const loadSession = async () => {
      try {
        // Load session
        const { data: sessionData, error: sessionError } = await supabase
          .from('sessions')
          .select('*')
          .eq('id', sessionId)
          .single();

        if (sessionError) {
          throw sessionError;
        }
        
        if (!sessionData) {
          throw new Error(t('session.invalid'));
        }
        
        if (!sessionData.is_active) {
          throw new Error(t('session.expired'));
        }
        
        if (new Date(sessionData.expires_at) < new Date()) {
          throw new Error(t('session.expired'));
        }

        setSession(sessionData);

        // Load entries
        const { data: entriesData, error: entriesError } = await supabase
          .from('entries')
          .select('*')
          .eq('session_id', sessionId);

        if (entriesError) throw entriesError;
        setEntries(entriesData || []);

        // Load participants
        const { data: participantsData, error: participantsError } = await supabase
          .from('participants')
          .select('*')
          .eq('session_id', sessionId);

        if (participantsError) throw participantsError;
        setParticipants(participantsData || []);

        // Load existing summary if available
        const { data: summaryData } = await supabase
          .from('ai_summaries')
          .select('*')
          .eq('session_id', sessionId)
          .eq('payment_status', 'completed')
          .maybeSingle();
        
        if (summaryData) {
          setSummary(summaryData);
        }

        // Check if user was already a participant
        if (participant && participantsData?.some(p => p.id === participant.id)) {
          // User is returning to their session
          setIsLoading(false);
          return;
        }

        // If user has no participant info, check if they're the creator
        if (sessionData.creator_nickname && !participant) {
          const existingParticipant = participantsData?.find(
            p => p.nickname === sessionData.creator_nickname
          );
          if (existingParticipant) {
            setParticipant(existingParticipant);
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading session:', error);
        setError(error instanceof Error ? error.message : t('common.error'));
        setIsLoading(false);
      }
    };

    loadSession();

    // Set up real-time subscriptions
    const entriesChannel = supabase.channel('entries-' + sessionId);
    const participantsChannel = supabase.channel('participants-' + sessionId);
    const sessionsChannel = supabase.channel('sessions-' + sessionId);

    // Entries subscription
    entriesChannel
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'entries', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          if (payload.new) {
            const newEntry = payload.new as Entry;
            if (!entries.some(e => e.id === newEntry.id)) {
              addEntry(newEntry);
            }
          }
        }
      )
      .subscribe();

    // Participants subscription with all events
    participantsChannel
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'participants', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          if (payload.new) {
            const newParticipant = payload.new as Participant;
            addParticipant(newParticipant);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'participants', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          if (payload.new) {
            const updatedParticipant = payload.new as Participant;
            updateParticipant(updatedParticipant);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'participants', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          if (payload.old) {
            removeParticipant(payload.old.id);
          }
        }
      )
      .subscribe();

    // Session subscription
    sessionsChannel
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'sessions', filter: `id=eq.${sessionId}` },
        (payload) => {
          if (payload.new) {
            setSession(payload.new as Session);
          }
        }
      )
      .subscribe();

    // Cleanup subscriptions
    return () => {
      entriesChannel.unsubscribe();
      participantsChannel.unsubscribe();
      sessionsChannel.unsubscribe();
    };
  }, [sessionId, navigate, setSession, setEntries, setParticipants, addEntry, entries, participant, setParticipant, addParticipant, removeParticipant, updateParticipant, t]);

  const handleJoinSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session || !nickname.trim() || isJoiningSession) return;

    setIsJoiningSession(true);
    try {
      const { data, error } = await supabase
        .from('participants')
        .insert({
          session_id: session.id,
          nickname: nickname.trim()
        })
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setParticipant(data);
        addParticipant(data);
      }
    } catch (error) {
      console.error('Error joining session:', error);
      setError(t('common.error'));
    } finally {
      setIsJoiningSession(false);
    }
  };

  const handleRequestSummary = async (forceNew: boolean = false) => {
    if (!session || (!participant && !isViewOnly) || entries.length < 5) return;

    if (summary?.payment_status === 'completed' && !forceNew) {
      setShowSummaryModal(true);
      return;
    }

    setIsSummaryLoading(true);
    setSummaryError(null);
    setPaymentUrl(null);
    setShowSummaryModal(true);

    try {
      const response = await requestAIAnalysis(session.id, entries, forceNew);

      if (!response.success) {
        throw new Error(response.error || t('analysis.error'));
      }

      if (response.summary) {
        setSummary(response.summary);
      } else if (response.payment_url) {
        setPaymentUrl(response.payment_url);
      }
    } catch (error) {
      console.error('Error requesting summary:', error);
      setSummaryError(error instanceof Error ? error.message : t('analysis.error'));
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const handleFinishSession = async () => {
    if (!session || !participant || session.is_finished) return;

    try {
      const { error } = await supabase
        .from('sessions')
        .update({
          is_finished: true,
          finished_at: new Date().toISOString()
        })
        .eq('id', session.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error finishing session:', error);
      setError(t('common.error'));
    }
  };

  const handleResumeSession = async () => {
    if (!session || !participant) return;

    try {
      const { error } = await supabase
        .from('sessions')
        .update({
          is_finished: false,
          finished_at: null
        })
        .eq('id', session.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error resuming session:', error);
      setError(t('common.error'));
    }
  };

  const handleCloseSummary = () => {
    setShowSummaryModal(false);
    setSummaryError(null);
    setPaymentUrl(null);
  };

  const handleStartNewSession = () => {
    reset(); // Reset the store data
    navigate(`/${language}`); // Navigate to home page with correct language
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="text-center">
          <Loader className="mx-auto h-12 w-12 animate-spin text-primary-500" />
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-md text-center">
          <Brain className="mx-auto h-16 w-16 text-gray-400" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            {error || t('session.invalid')}
          </h2>
          <p className="mt-2 text-gray-600">
            {t('error.404.message')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 rounded-lg bg-primary-500 px-6 py-2 text-white hover:bg-primary-600 transition-colors"
          >
            {t('common.home')}
          </button>
        </div>
      </div>
    );
  }

  if (!participant && !isViewOnly) {
    if (isJoining || !session?.creator_nickname) {
      return (
        <div className="container mx-auto px-4 py-8">
          <div className="mx-auto max-w-md">
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('session.join.title')}: {session?.topic}
              </h2>
              {session?.is_finished ? (
                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    {t('session.finished')}
                  </p>
                  <button
                    onClick={() => navigate('/')}
                    className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    {t('common.home')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleJoinSession} className="space-y-4">
                  <div>
                    <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('home.nickname.label')}
                    </label>
                    <input
                      type="text"
                      id="nickname"
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      placeholder={t('home.nickname.placeholder')}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isJoiningSession || !nickname.trim()}
                    className="w-full bg-primary-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
                  >
                    {isJoiningSession ? t('common.loading') : t('session.join.button')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    if (session?.creator_nickname) {
      const existingParticipant = participants.find(
        p => p.nickname === session.creator_nickname
      );
      if (existingParticipant) {
        setParticipant(existingParticipant);
      }
    }
  }

  const isCreator = participant?.nickname === session?.creator_nickname;

  return (
    <>
      <ScrollingAlphabet />
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mt-2">
              {session.topic}
            </h1>
            <p className="text-gray-600 mt-1">
              {t('session.session_of')} {participant?.nickname || session.creator_nickname}
            </p>
            {isViewOnly && (
              <p className="text-sm text-accent-600 mt-1">
                {t('session.readonly')}
              </p>
            )}
            {session.is_finished && (
              <p className="text-sm text-accent-600 mt-1">
                {t('session.finished')}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <SessionTimer
              startTime={session.created_at}
              expiresAt={session.expires_at}
            />
            {!isViewOnly && (
              <>
                <ExportButton 
                  entries={entries}
                  summary={summary}
                  topic={session.topic}
                />
                <ShareButton
                  sessionId={session.id}
                  isCreator={participant?.nickname === session.creator_nickname}
                />
              </>
            )}
          </div>
        </div>

        {session.is_finished && (
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-gray-700">{t('session.finished_message')}</p>
              <button
                onClick={handleStartNewSession}
                className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-white hover:bg-primary-600 transition-colors"
              >
                <PlusCircle className="h-5 w-5" />
                <span>{t('session.start_new_session')}</span>
              </button>
            </div>
          </div>
        )}

        <SessionStatus
          entries={entries}
          participants={participants}
          maxParticipants={session.max_participants}
          onRequestSummary={handleRequestSummary}
          onFinishSession={handleFinishSession}
          onResumeSession={handleResumeSession}
          isViewOnly={isViewOnly}
          summary={summary}
          isFinished={session.is_finished}
          isCreator={isCreator}
          sessionId={session.id}
          participantId={participant?.id}
        />

        <div className="grid gap-4 sm:gap-6 pt-4 lg:grid-cols-[1fr,300px]">
          <LetterGrid
            sessionId={session.id}
            participant={participant}
            entries={entries}
            participants={participants}
            onNewEntry={addEntry}
            onInputFocus={() => setIsInputFocused(true)}
            onInputBlur={() => setIsInputFocused(false)}
            isViewOnly={isViewOnly || session.is_finished}
          />
          {!isViewOnly && (
            <div className="-mt-4 sm:-mt-6">
              <ParticipantList
                participants={participants}
                currentParticipant={participant}
                session={session}
              />
            </div>
          )}
        </div>

        {showSummaryModal && (
          <SummaryModal
            summary={summary}
            isLoading={isSummaryLoading}
            error={summaryError}
            paymentUrl={paymentUrl}
            onClose={handleCloseSummary}
            onRefresh={() => handleRequestSummary(true)}
          />
        )}
      </div>
    </>
  );
}